import { Component, Injector, OnInit } from '@angular/core';
import { HttpService } from '@app/@core/services/http.service';
import { forkJoin } from 'rxjs';
import { LocalService } from '@app/@core/services/local.service';
import { DataStoreService } from '@pics-core/dynamic';
import { ActivatedRoute, Router } from '@angular/router';
import { MyApplicationsService } from '@app/pages/my-application/my-app-service.service';
import { take } from 'rxjs';
interface TimelineItem {
  status: string;
  date: string;
  name: string;
}

interface ApiTimelineItem {
  application_id: number;
  case_id: string;
  cd_background_status: string;
  occurred_date: string;
  person_id: number;
  is_active: boolean;
  created_by: number;
  updated_by: number;
}

@Component({
  standalone: false,
  selector: 'app-case-flow-timeline',
  templateUrl: './case-flow-timeline.component.html',
  styleUrls: ['./case-flow-timeline.component.scss']
})
export class CaseFlowTimelineComponent implements OnInit {
  timelineData: TimelineItem[] = [];
  backgroundStatusOptions: any[] = [];
  personData: any = null;
  userData: any = null;
  applicationID: any = null;
  userID: any = null;
  createdByUser: any = null;
  injector: Injector;
  dataStore: DataStoreService;
  hidePreviousNextButtons: boolean = false;
  private userCache: Map<number, any> = new Map();
  constructor(
    private readonly httpService: HttpService,
    private readonly sessionStorage: LocalService,
    public router: Router,
    injector: Injector,
     private route: ActivatedRoute,
     private appService : MyApplicationsService
  ) {
    this.injector = injector;
    this.dataStore = injector.get<DataStoreService>(DataStoreService);
    this.checkReadOnlyModeToHidePreviousNextButton();
  }

  ngOnInit(): void {
    this.getPersonDataFromSession();
    this.getApplicationIDFromSession();
    this.loadData();
  }

  private loadData(): void {
    if (!this.applicationID) {
      console.error('Application ID not found in session storage');
      this.timelineData = [];
      return;
    }

    console.log('Loading background case timeline data for applicationID:', this.applicationID);

    // Load both application status options and timeline data simultaneously
    forkJoin([
      this.httpService.get('/domain/lookup?requestType=BG_STATUS'),
      this.httpService.get(`/application-flow/backgroundcaseTimelineData?applicationId=${this.applicationID}`)
    ]).subscribe({
      next: (results: any[]) => {
        // Load application status options from first response
        this.backgroundStatusOptions = results[0].data;
        console.log('Background status options loaded:', this.backgroundStatusOptions);

        // Process timeline data from second response
        const timelineResponse = results[1];
        console.log('Background case timeline response:', timelineResponse);

        if (timelineResponse.status === 'success' && timelineResponse.data?.data) {
          this.processTimelineData(
            Array.isArray(timelineResponse.data.data) ? timelineResponse.data.data : [timelineResponse.data.data]
          );
        } else {
          console.warn('No timeline data found or invalid response structure');
          this.timelineData = [];
        }
      },
      error: error => {
        console.error('Error loading background case timeline data:', error);
        this.timelineData = [];
      }
    });
  }

  private getPersonDataFromSession(): void {
    const personDataString = this.sessionStorage.getItem('personData');
    this.personData = personDataString ? JSON.parse(personDataString) : null;
  }

  private getApplicationIDFromSession(): void {
    const applicationIDString = this.sessionStorage.getItem('applicationID');
    this.applicationID = applicationIDString ? JSON.parse(applicationIDString) : null;
  }
  private processTimelineData(apiData: ApiTimelineItem[]): void {
    console.log('Processing background case timeline data:', apiData);

    // Sort by occurred_date in descending order (newest first/top, oldest last/bottom)
    const sortedData = apiData.sort((a, b) => {
      const dateA = new Date(a.occurred_date).getTime();
      const dateB = new Date(b.occurred_date).getTime();
      return dateB - dateA; // Descending order: newest first, oldest last
    });

    // Fetch user data for all unique users and then process timeline
    const uniqueUserIds = [...new Set(sortedData.map(item => item.updated_by ? item.updated_by : item.created_by))];
    this.fetchUserDataForIds(uniqueUserIds).then(() => {
      this.timelineData = sortedData.map(item => {
        const statusLabel = this.getStatusLabel(item.cd_background_status);
        const formattedDate = this.formatDate(item.occurred_date);
        const personName = this.getPersonName(item.updated_by ? item.updated_by : item.created_by);

        return {
          status: (statusLabel),
          date: formattedDate,
          name: this.toTitleCase(personName)
        };
      });

      console.log('Processed background case timeline data:', this.timelineData);
    });
  }

  private getStatusLabel(statusCode: string): string {
    return this.getBackgroundStatusDisplayValue(statusCode);
  }

  getBackgroundStatusDisplayValue(key: string): string {
    if (!this.backgroundStatusOptions || !key) {
      return key || '';
    }

    const caseStatus = this.backgroundStatusOptions.find(status => status.key === key);
    return caseStatus ? caseStatus.value : key;
  }

  private formatDate(dateString: string): string {
    const date = new Date(dateString);
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December'
    ];

    const month = months[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, '0');

    return `${month} ${day}, ${year}, ${displayHours}:${displayMinutes} ${ampm}`;
  }

  private getPersonName(userID: number): string {  
    const userData = this.userCache.get(userID);

    if (!userData) {
      return 'Unknown User';
    }

    const firstName = userData.firstname || '';
    const lastName = userData.lastname || '';

    return `${firstName} ${lastName}`.trim() || 'Unknown User';
  }

  private toTitleCase(str: string): string {
    return str.toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  }

  private fetchUserDataForIds(userIds: number[]): Promise<void> {
    const requests = userIds
      .filter(id => !this.userCache.has(id)) // Only fetch if not already cached
      .map(id =>
        this.httpService
          .get(`/application-flow/user?userId=${id}`)
          .toPromise()
          .then((response: any) => {
            if (response?.data) {
              this.userCache.set(id, response.data[0]);
            }
          })
          .catch((error: any) => {
            console.error(`Error fetching user data for userId ${id}:`, error);
            this.userCache.set(id, { first_name: 'Unknown', last_name: 'User' });
          })
      );

    return Promise.all(requests).then(() => {});
  }

  isSingleItem(): boolean {
    return this.timelineData.length === 1;
  }
  moveToNext() {
    const tabList = JSON.parse(this.dataStore.getData('TABCONFIGLIST') || '[]');
    const currentTabIndex = tabList?.findIndex(tab => tab.id == 3550);
    const parentGridPageInfo = this.sessionStorage.getObj('backToGridPage')
      ? this.sessionStorage.getObj('backToGridPage')
      : this.sessionStorage.getObj('parentGridPage')
      ? this.sessionStorage.getObj('parentGridPage')
      : {};
    let nextTab = null;
    let islastTab = null;
    if (currentTabIndex !== -1 && currentTabIndex == tabList.length - 1) {
      islastTab = tabList[currentTabIndex + 1] ? false : true;
    }
    currentTabIndex !== -1 ? (nextTab = tabList[currentTabIndex + 1]) : null;
    let lastTabId = null;
    if (islastTab) {
      lastTabId = tabList[currentTabIndex]?.id;
    }
    const nextTabIndex = currentTabIndex + 1;
    if (islastTab) {
      if (parentGridPageInfo.id) {
        this.router.navigate([`/pages/dynamic-search/search/${parentGridPageInfo?.id}`], { relativeTo: this.route });
      } else {
        this.router.navigate([`/pages/${parentGridPageInfo.name}`], { relativeTo: this.route });
      }
    } else if (nextTab && nextTab.id) {
      this.dataStore.setData('FORM_SUBMISSION_SUCCESS', { isRoute: true, nextPage: nextTabIndex, submissionId: null });
      return;
    } else {
      // this.location.back();
      return;
    }
  }
  moveToPrevious() {
    const tabList = JSON.parse(this.dataStore.getData('TABCONFIGLIST') || '[]');
    const currentTabIndex = tabList?.findIndex(tab => tab.id == 3550);
    const parentGridPageInfo = this.sessionStorage.getObj('backToGridPage')
      ? this.sessionStorage.getObj('backToGridPage')
      : this.sessionStorage.getObj('parentGridPage')
      ? this.sessionStorage.getObj('parentGridPage')
      : {};
    let previousTab = null;
    let isFirstTab = null;
    if (currentTabIndex === 0) {
      isFirstTab = true;
    }
    currentTabIndex > 0 ? (previousTab = tabList[currentTabIndex - 1]) : null;
    const previousTabIndex = currentTabIndex - 1;
    if (isFirstTab) {
      if (parentGridPageInfo.id) {
        this.router.navigate([`/pages/dynamic-search/search/${parentGridPageInfo?.id}`], { relativeTo: this.route });
      } else {
        this.router.navigate([`/pages/${parentGridPageInfo.name}`], { relativeTo: this.route });
      }
    } else if (previousTab && previousTab.id) {
      this.dataStore.setData('FORM_SUBMISSION_SUCCESS', {
        isRoute: true,
        nextPage: previousTabIndex,
        submissionId: null
      });
      return;
    } else {
      return;
    }
  }

  checkReadOnlyModeToHidePreviousNextButton() {
    let appMode = JSON.parse(this.sessionStorage.getItem('applicationmode'));
    let isReadOnlyMode = false;
    if (appMode === 'view') {
      isReadOnlyMode = true;
    }
    if (isReadOnlyMode) {
      this.hidePreviousNextButtons = true;
    }
  }
}
